import { ClockCircleOutlined, ShoppingOutlined } from "@ant-design/icons";
import { Button, Card, Image, Space, Tag, Typography } from "antd";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

import { buildAccountOrderDetailPath } from "@/constants";
import type { OrderSummary } from "@/types";
import { formatCurrency, formatDateTime } from "@/utils/format";
import {
  formatOrderPaymentState,
  formatOrderStatus,
  getOrderStatusColor,
} from "@/utils/order";

const { Paragraph, Text, Title } = Typography;

interface AccountOrderCardProps {
  order: OrderSummary;
}

const summarizeItems = (order: OrderSummary) => {
  if (order.items_count <= 1) {
    return null;
  }

  return `Bao gồm ${order.items_count} sản phẩm`;
};

export const AccountOrderCard = ({ order }: AccountOrderCardProps) => {
  const hasImage = !!(
    order.image &&
    typeof order.image === "string" &&
    order.image.trim() !== "" &&
    order.image !== "null" &&
    order.image !== "undefined"
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.24 }}
    >
      <Card className="account-order-card">
        <div className="account-order-card__body">
          <div className="account-order-card__media">
            {hasImage ? (
              <Image
                alt={order.name || order.order_number}
                className="account-order-card__image"
                preview={false}
                src={order.image}
              />
            ) : (
              <div className="account-order-card__image-placeholder">
                <ShoppingOutlined />
              </div>
            )}
          </div>

          <div className="account-order-card__content">
            <div className="account-order-card__header">
              <div>
                <Title level={5}>Đơn hàng {order.order_number}</Title>
                <Space size={8} wrap>
                  <Text type="secondary">
                    <ClockCircleOutlined />{" "}
                    {formatDateTime(order.created_at ?? "")}
                  </Text>
                  <Text type="secondary">
                    Thanh toán:{" "}
                    {formatOrderPaymentState(order.payment_status ?? "pending")}
                  </Text>
                </Space>
              </div>

              <Tag color={getOrderStatusColor(order.status)}>
                {formatOrderStatus(order.status)}
              </Tag>
            </div>

            <div className="account-order-card__summary">
              <div className="account-order-card__product">
                <Text strong>{order.name ?? "Sản phẩm trong đơn"}</Text>
                {summarizeItems(order) ? (
                  <Paragraph type="secondary">
                    {summarizeItems(order)}
                  </Paragraph>
                ) : null}
              </div>

              <div className="account-order-card__price">
                <Text type="secondary">Tổng thanh toán</Text>
                <Text strong className="gold-text">
                  {formatCurrency(Number(order.grand_total ?? 0))}
                </Text>
              </div>
            </div>

            <div className="account-order-card__footer">
              <Text type="secondary">
                Giao hàng:{" "}
                {formatOrderStatus(order.fulfillment_status ?? "pending")}
              </Text>

              <Link to={buildAccountOrderDetailPath(order.order_id)}>
                <Button type="primary">Xem chi tiết</Button>
              </Link>
            </div>
          </div>
        </div>
      </Card>
    </motion.div>
  );
};
